// WhatsApp completo na tela (23/10/2026): texto, foto, vídeo, documento,
// áudio, mensagem de voz, figurinha, responder citando e reagir.
//
// Usado pelo Inbox e pela aba WhatsApp da negociação. O que muda entre os dois
// é só o "transporte" — qual edge function chamar e com qual identificador:
//   - Inbox: `inbox-send` com { conversation_id } (permissão pela RLS do Inbox);
//   - negociação: `crm-conversas-negociacao` com { negociacaoId, conversationId }
//     (adm, gerente, responsável).
// As ações em si moram no servidor, em `_shared/acoesConversa.ts`.
//
// Arquivo NÃO passa pela edge function: pede-se um link de upload, o
// navegador sobe direto para o bucket e só então a mensagem é enviada
// apontando para o caminho.

import { useCallback, useState } from 'react';
import { supabase } from '../../../src/lib/supabase';
import type { Anexo, Mensagem } from './tipos';

export interface Figurinha {
  caminho: string;
  url: string;
}

export interface ReacoesDaMensagem {
  cliente?: { emoji: string };
  nos?: { emoji: string; por?: string | null };
}

export interface AcoesConversa {
  enviarTexto(texto: string, responderA?: string | null): Promise<Mensagem>;
  enviarArquivo(
    arquivo: Blob,
    opcoes: { nome: string; legenda?: string; voz?: boolean; figurinha?: boolean; responderA?: string | null },
  ): Promise<Mensagem>;
  enviarFigurinhaExistente(caminho: string, responderA?: string | null): Promise<Mensagem>;
  reagir(mensagemId: string, emoji: string): Promise<{ reacoes?: ReacoesDaMensagem }>;
  listarFigurinhas(): Promise<Figurinha[]>;
}

/** Chama a edge function do lugar (Inbox ou negociação) com o corpo da ação. */
export type Transporte = (corpo: Record<string, unknown>) => Promise<any>;

/** Erro com a mensagem que o servidor escreveu (o SDK só diz "non-2xx"). */
export async function invocar(funcao: string, corpo: Record<string, unknown>): Promise<any> {
  const { data, error } = await supabase.functions.invoke(funcao, { body: corpo });
  if (error) {
    const detalhe = await (error as any)?.context?.json?.().catch(() => null);
    throw new Error(detalhe?.error || 'Não foi possível concluir. Tente de novo.');
  }
  if (data && data.ok === false) throw new Error(data.error || 'Não foi possível concluir.');
  return data;
}

export const transporteInbox = (conversationId: string): Transporte =>
  (corpo) => invocar('inbox-send', { conversation_id: conversationId, ...corpo });

export const transporteNegociacao = (negociacaoId: number, conversationId: string): Transporte =>
  (corpo) => invocar('crm-conversas-negociacao', { negociacaoId, conversationId, ...corpo });

/** A linha que volta do servidor, no formato da tela. */
function comoMensagem(bruta: any): Mensagem {
  return {
    subject: null,
    body_html: null,
    body_visible: null,
    recipients: null,
    provider_message_id: null,
    failure_reason: null,
    anexos: [],
    ...bruta,
  } as Mensagem;
}

export function criarAcoes(chamar: Transporte): AcoesConversa {
  const enviar = async (corpo: Record<string, unknown>) => {
    const r = await chamar({ acao: 'enviar', ...corpo });
    return comoMensagem(r.message ?? r.mensagem);
  };

  return {
    enviarTexto: (texto, responderA) => enviar({ texto, responderA: responderA ?? null }),

    async enviarArquivo(arquivo, opcoes) {
      const mime = arquivo.type || 'application/octet-stream';
      const prep = await chamar({
        acao: 'preparar_anexo',
        arquivo: { nome: opcoes.nome, mime, tamanho: arquivo.size, voz: !!opcoes.voz, figurinha: !!opcoes.figurinha },
      });
      const { error } = await supabase.storage
        .from('inbox-media')
        .uploadToSignedUrl(prep.caminho, prep.token, arquivo, { contentType: mime });
      if (error) throw new Error(`Não foi possível subir o arquivo: ${error.message}`);
      return enviar({
        texto: opcoes.legenda ?? '',
        anexo: { caminho: prep.caminho, nome: opcoes.nome, mime, tamanho: arquivo.size },
        voz: !!opcoes.voz,
        figurinha: !!opcoes.figurinha,
        responderA: opcoes.responderA ?? null,
      });
    },

    enviarFigurinhaExistente: (caminho, responderA) =>
      enviar({ figurinha: true, anexo: { caminho, nome: 'figurinha.webp', mime: 'image/webp' }, responderA: responderA ?? null }),

    async reagir(mensagemId, emoji) {
      const r = await chamar({ acao: 'reagir', mensagemId, emoji });
      return { reacoes: r?.payload?.reacoes };
    },

    async listarFigurinhas() {
      const r = await chamar({ acao: 'figurinhas' });
      return (r?.figurinhas ?? []).map((f: any) => ({ caminho: f.caminho, url: f.url }));
    },
  };
}

// ─── Tipo de mensagem a partir do arquivo (para a mensagem aparecer na hora) ──

export function tipoLocal(mime: string, opcoes: { voz?: boolean; figurinha?: boolean } = {}): Mensagem['type'] {
  const m = (mime || '').toLowerCase();
  if (opcoes.figurinha) return 'sticker';
  if (opcoes.voz || m.startsWith('audio/')) return 'audio';
  if (m.startsWith('image/') && !m.includes('svg') && !m.includes('heic')) return 'image';
  if (m.startsWith('video/')) return 'video';
  return 'document';
}

export interface ItemParaEnviar {
  arquivo: Blob;
  nome: string;
  voz?: boolean;
  figurinha?: boolean;
}

/**
 * Envio com a mensagem aparecendo na hora (otimista), igual para o Inbox e
 * para a negociação. Quem usa só diz como aplicar a mudança na lista.
 *
 * Arquivo: aparece com a prévia local (blob) e, quando o servidor confirma,
 * a mensagem real entra no lugar mantendo a prévia — o link assinado só vem
 * na próxima leitura. Falha: a mensagem fica marcada com o motivo (apagar
 * faria a pessoa perder o que mandou).
 */
export function useEnvioRico(p: {
  acoes: AcoesConversa | null;
  conversaId: string | null;
  userId: string | null;
  aplicar: (fn: (atuais: Mensagem[]) => Mensagem[]) => void;
  aoEnviar?: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const local = useCallback((parcial: Partial<Mensagem>): Mensagem => ({
    id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    conversation_id: p.conversaId ?? '',
    direction: 'outbound',
    type: 'text',
    body: null,
    body_visible: null,
    subject: null,
    body_html: null,
    recipients: null,
    provider_message_id: null,
    provider_timestamp: new Date().toISOString(),
    status: 'queued',
    failure_reason: null,
    sender_user_id: p.userId,
    anexos: [],
    local: true,
    ...parcial,
  }), [p.conversaId, p.userId]);

  const executar = useCallback(async (provisoria: Mensagem, acao: () => Promise<Mensagem>) => {
    p.aplicar((atuais) => [...atuais, provisoria]);
    try {
      const real = await acao();
      p.aplicar((atuais) => {
        const sem = atuais.filter((m) => m.id !== provisoria.id && m.id !== real.id);
        // Mantém a prévia local do arquivo até a próxima leitura trazer o link assinado.
        const anexos = real.anexos?.length ? real.anexos : provisoria.anexos;
        return [...sem, { ...real, anexos, local: false }].sort((a, b) => a.provider_timestamp.localeCompare(b.provider_timestamp));
      });
      p.aoEnviar?.();
    } catch (e) {
      const motivo = (e as Error).message;
      setErro(motivo);
      p.aplicar((atuais) => atuais.map((m) => (m.id === provisoria.id ? { ...m, status: 'failed', failure_reason: motivo } : m)));
    }
  }, [p]);

  const enviarTexto = useCallback(async (texto: string, responderA?: Mensagem | null) => {
    if (!p.acoes || !texto.trim()) return;
    setEnviando(true);
    setErro(null);
    await executar(
      local({ body: texto, reply_to_external_id: responderA?.provider_message_id ?? null }),
      () => p.acoes!.enviarTexto(texto, responderA?.id ?? null),
    );
    setEnviando(false);
  }, [p.acoes, executar, local]);

  const enviarArquivos = useCallback(async (itens: ItemParaEnviar[], legenda: string, responderA?: Mensagem | null) => {
    if (!p.acoes || !itens.length) return;
    setEnviando(true);
    setErro(null);
    // Um por vez, na ordem escolhida — é como o app entrega. A legenda vai com o primeiro.
    for (const [i, item] of itens.entries()) {
      const url = URL.createObjectURL(item.arquivo);
      const anexo: Anexo = {
        id: `local-anexo-${i}`, message_id: '', storage_path: '', mime_type: item.arquivo.type || null,
        file_name: item.nome, size_bytes: item.arquivo.size, url,
      };
      await executar(
        local({
          type: tipoLocal(item.arquivo.type, item),
          body: i === 0 && !item.voz && !item.figurinha ? legenda || null : null,
          reply_to_external_id: i === 0 ? responderA?.provider_message_id ?? null : null,
          anexos: [anexo],
        }),
        () => p.acoes!.enviarArquivo(item.arquivo, {
          nome: item.nome,
          legenda: i === 0 ? legenda : '',
          voz: item.voz,
          figurinha: item.figurinha,
          responderA: i === 0 ? responderA?.id ?? null : null,
        }),
      );
    }
    setEnviando(false);
  }, [p.acoes, executar, local]);

  const enviarFigurinha = useCallback(async (fig: Figurinha, responderA?: Mensagem | null) => {
    if (!p.acoes) return;
    setErro(null);
    await executar(
      local({
        type: 'sticker',
        reply_to_external_id: responderA?.provider_message_id ?? null,
        anexos: [{ id: 'local-fig', message_id: '', storage_path: fig.caminho, mime_type: 'image/webp', file_name: null, size_bytes: null, url: fig.url }],
      }),
      () => p.acoes!.enviarFigurinhaExistente(fig.caminho, responderA?.id ?? null),
    );
  }, [p.acoes, executar, local]);

  const reagir = useCallback(async (mensagem: Mensagem, emoji: string) => {
    if (!p.acoes) return;
    const atual = (mensagem.payload as any)?.reacoes?.nos?.emoji;
    const novo = atual === emoji ? '' : emoji; // tocar no mesmo emoji tira a reação, como no app
    const aplicarReacao = (r: ReacoesDaMensagem | undefined) =>
      p.aplicar((atuais) => atuais.map((m) => (m.id === mensagem.id ? { ...m, payload: { ...(m.payload ?? {}), reacoes: r } } : m)));
    const antes = (mensagem.payload as any)?.reacoes as ReacoesDaMensagem | undefined;
    const otimista: ReacoesDaMensagem = { ...(antes ?? {}) };
    if (novo) otimista.nos = { emoji: novo, por: p.userId };
    else delete otimista.nos;
    aplicarReacao(otimista);
    try {
      const r = await p.acoes.reagir(mensagem.id, novo);
      aplicarReacao(r.reacoes ?? otimista);
    } catch (e) {
      aplicarReacao(antes);
      setErro((e as Error).message);
    }
  }, [p.acoes, p.userId, p]);

  return { enviarTexto, enviarArquivos, enviarFigurinha, reagir, enviando, erro, limparErro: () => setErro(null) };
}

// ─── Citação ("responder") ──────────────────────────────────────────────────

const ROTULO_TIPO: Record<string, string> = {
  image: '📷 Foto', video: '🎥 Vídeo', audio: '🎤 Áudio', document: '📄 Documento', sticker: 'Figurinha', location: '📍 Localização',
};

/** O que mostrar de uma mensagem quando ela é citada. */
export function textoParaCitacao(m: Mensagem): string {
  return (m.body_visible ?? m.body)?.trim() || ROTULO_TIPO[m.type] || 'Mensagem';
}

/**
 * Quem disse e o quê, para a citação no balão e para a faixa "respondendo a".
 * Mensagem citada que não está carregada na tela (mais antiga que a página)
 * aparece como "mensagem anterior", sem inventar conteúdo.
 */
export function resumoCitacao(
  alvo: Mensagem | null | undefined,
  nomes: Record<string, string>,
  nomeContato: string | null | undefined,
): { autor: string; texto: string } {
  if (!alvo) return { autor: 'Mensagem anterior', texto: 'Não está carregada nesta tela.' };
  const autor = alvo.direction === 'outbound'
    ? (alvo.sender_user_id && nomes[alvo.sender_user_id]) || 'Você'
    : nomeContato?.trim() || 'Cliente';
  return { autor, texto: textoParaCitacao(alvo) };
}

/** Índice provider_message_id → mensagem, para achar a citada. */
export function indicePorProvider(mensagens: Mensagem[]): Map<string, Mensagem> {
  const m = new Map<string, Mensagem>();
  for (const x of mensagens) if (x.provider_message_id) m.set(x.provider_message_id, x);
  return m;
}
